import React, { ImgHTMLAttributes, useMemo, useRef } from 'react';
import {
  FormRow,
  FormLabel,
  FormFieldWrapper,
  FormRowProps
} from '../form-input';
import { SvgLoader } from '../svg-loader';
import {
  getWidgetCdnPath,
  useRetailerFileUpload,
  FilePicker,
  getCdnPath,
  getS3Path,
  FilePickerRef
} from 'framework';
import {
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import { Clear, Upload } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export const icons = [
  {
    name: 'Faslet',
    url: `${getWidgetCdnPath()}/button-icons/f-logo-recolor.svg`
  },
  {
    name: 'Tape Tall',
    url: `${getWidgetCdnPath()}/button-icons/tape-tall-recolor.svg`
  },
  {
    name: 'Tape Wide',
    url: `${getWidgetCdnPath()}/button-icons/tape-wide-recolor.svg`
  },
  {
    name: 'Hanger',
    url: `${getWidgetCdnPath()}/button-icons/hanger-recolor.svg`
  }
];

interface CustomIcon {
  customIcon?: string;
  disableIcon?: boolean;
}

interface CustomIconFieldProps extends FormRowProps {
  label?: string;
  value?: CustomIcon;
  onChange?: (value: CustomIcon) => void;
  retailerSlug: string;
}

/**
 * Specify either a None, a predefined icon, or a custom icon URL.
 * Choosing None emits { disableIcon: true }.
 * Choosing a predefined icon emits { customIcon: '/path' } in the widget cdn.
 * Choosing a custom icon uploads the file to s3 then emits { customIcon: '/path' } where the path is s3 storage.
 */
export function CustomIconField(props: CustomIconFieldProps) {
  const { label, value, onChange, retailerSlug, ...formRowProps } = props;
  const { t } = useTranslation();

  const filePickerRef = useRef<FilePickerRef>(null);

  const { put } = useRetailerFileUpload({
    slug: retailerSlug,
    fileTag: `custom-icon-${Date.now()}`
  });

  const handleFileSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const result = await put(file);
    if (!result) return;

    const newValue: CustomIcon = {
      customIcon: result.cdnUrl,
      disableIcon: false
    };

    onChange?.(newValue);
  };

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: string) => {
    if (newValue === 'None') {
      onChange?.({ disableIcon: true, customIcon: undefined });
      return;
    }

    // handled separately
    if (newValue === 'Custom') return;

    const selectedIcon = icons.find(icon => icon.name === newValue);
    if (!selectedIcon) return;

    onChange?.({
      customIcon: selectedIcon.url,
      disableIcon: false
    });
  };

  const handleCustomIconClick = () => filePickerRef.current?.open();

  const currentIcon = useMemo(() => {
    if (value?.disableIcon) return 'None';

    const foundIcon = icons.find(icon => icon.url === value?.customIcon);
    if (foundIcon) return foundIcon.name;

    return 'Custom';
  }, [value]);

  const customImageSrc = useMemo(
    () => value?.customIcon?.replace(getCdnPath(), getS3Path()) ?? '',
    [value]
  );

  return (
    <FormRow {...formRowProps}>
      <FormLabel>{label}</FormLabel>
      <FormFieldWrapper>
        <ToggleButtonGroup
          value={currentIcon}
          exclusive
          onChange={handleChange}
          aria-label="icon selection"
        >
          {icons.map(icon => (
            <Tooltip title={icon.name} key={`tooltip-${icon.name}`}>
              <ToggleButton
                key={icon.name}
                value={icon.name}
                aria-label={icon.name}
              >
                <SvgLoader
                  url={icon.url}
                  alt={icon.name}
                  width="24px"
                  height="24px"
                />
              </ToggleButton>
            </Tooltip>
          ))}
        </ToggleButtonGroup>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

        <ToggleButtonGroup
          value={currentIcon}
          exclusive
          onChange={handleChange}
          aria-label="preset icon selection"
        >
          <Tooltip title={t('noIcon')}>
            <ToggleButton value="None" aria-label="None">
              <Clear style={{ width: '24px', height: '24px' }} />
            </ToggleButton>
          </Tooltip>

          <Tooltip title={t('uploadCustomIcon')}>
            <ToggleButton
              value="Custom"
              aria-label="Custom"
              onClick={handleCustomIconClick}
            >
              {currentIcon == 'Custom' && customImageSrc ? (
                <CustomIcon
                  src={customImageSrc}
                  alt="Custom Icon"
                  width="24px"
                  height="24px"
                />
              ) : (
                <Upload style={{ width: '24px', height: '24px' }} />
              )}
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <FilePicker
          ref={filePickerRef}
          onFileSelected={handleFileSelected}
          style={{ display: 'none' }}
          accept={'image/svg+xml, image/png, image/jpeg'}
        />
      </FormFieldWrapper>
    </FormRow>
  );
}

function CustomIcon(props: ImgHTMLAttributes<HTMLImageElement>) {
  const { src, width, height, ...rest } = props;

  if (!src || !src.startsWith('.svg')) {
    return (
      <span style={{ display: 'inline-block', width, height }}>
        <img
          src={src}
          style={{ maxWidth: width, maxHeight: height }}
          {...rest}
        />
      </span>
    );
  }

  return <SvgLoader url={src} width={width} height={height} {...rest} />;
}
